import { useState, useEffect, useCallback } from 'react'
import { aiApi } from '../api'
import { Card, Toggle, SettingsRow, Modal } from './UI'
import { ConfirmModal, Loader } from '../ui'
import { Spinner } from '../hooks/useApi.jsx'
import { HiCheckCircle, HiXCircle, HiKey, HiSparkles, HiArrowPath, HiTrash, HiChevronDown, HiChevronUp, HiPlus, HiStar, HiBars3 } from 'react-icons/hi2'

const PROVIDERS = [
  { id: 'openrouter', label: 'OpenRouter (Barcha modellar)' },
  { id: 'anthropic', label: 'Anthropic (Claude)' },
  { id: 'openai', label: 'OpenAI (GPT)' },
  { id: 'gemini', label: 'Google Gemini' },
]

const OPENROUTER_GROUPS = [
  { label: 'Anthropic — Claude', models: [
    { id: 'anthropic/claude-sonnet-4-5', label: 'Claude Sonnet 4.5 (Eng yangi)', supports_chat: true, supports_vision: true, supports_voice: false },
    { id: 'anthropic/claude-opus-4-5', label: 'Claude Opus 4.5 (Premium)', supports_chat: true, supports_vision: true, supports_voice: false },
    { id: 'anthropic/claude-3-7-sonnet', label: 'Claude 3.7 Sonnet', supports_chat: true, supports_vision: true, supports_voice: false },
    { id: 'anthropic/claude-3-5-sonnet', label: 'Claude 3.5 Sonnet', supports_chat: true, supports_vision: true, supports_voice: false },
    { id: 'anthropic/claude-3-5-haiku', label: 'Claude 3.5 Haiku (Tez)', supports_chat: true, supports_vision: true, supports_voice: false },
  ]},
  { label: 'OpenAI — GPT', models: [
    { id: 'openai/gpt-4o', label: 'GPT-4o', supports_chat: true, supports_vision: true, supports_voice: false },
    { id: 'openai/gpt-4o-mini', label: 'GPT-4o Mini (Arzon)', supports_chat: true, supports_vision: true, supports_voice: false },
    { id: 'openai/o3-mini', label: 'o3 Mini (Fikrlovchi)', supports_chat: true, supports_vision: false, supports_voice: false },
    { id: 'openai/o1', label: 'o1', supports_chat: true, supports_vision: false, supports_voice: false },
  ]},
  { label: 'Google — Gemini', models: [
    { id: 'google/gemini-2.5-pro', label: 'Gemini 2.5 Pro (Eng aqlli)', supports_chat: true, supports_vision: true, supports_voice: false },
    { id: 'google/gemini-2.5-flash', label: 'Gemini 2.5 Flash (Tez)', supports_chat: true, supports_vision: true, supports_voice: false },
    { id: 'google/gemini-2.0-flash', label: 'Gemini 2.0 Flash', supports_chat: true, supports_vision: true, supports_voice: false },
  ]},
  { label: 'Meta — Llama', models: [
    { id: 'meta-llama/llama-3.3-70b-instruct', label: 'Llama 3.3 70B (Bepul)', supports_chat: true, supports_vision: false, supports_voice: false },
    { id: 'meta-llama/llama-3.1-8b-instruct', label: 'Llama 3.1 8B (Juda tez)', supports_chat: true, supports_vision: false, supports_voice: false },
    { id: 'meta-llama/llama-3.2-11b-vision-instruct', label: 'Llama 3.2 11B Vision', supports_chat: true, supports_vision: true, supports_voice: false },
  ]},
  { label: 'Mistral AI', models: [
    { id: 'mistralai/mistral-large', label: 'Mistral Large', supports_chat: true, supports_vision: false, supports_voice: false },
    { id: 'mistralai/mistral-nemo', label: 'Mistral Nemo (Bepul)', supports_chat: true, supports_vision: false, supports_voice: false },
  ]},
  { label: 'DeepSeek', models: [
    { id: 'deepseek/deepseek-chat', label: 'DeepSeek V3 (Arzon)', supports_chat: true, supports_vision: false, supports_voice: false },
    { id: 'deepseek/deepseek-r1', label: 'DeepSeek R1 (Fikrlovchi)', supports_chat: true, supports_vision: false, supports_voice: false },
  ]},
  { label: 'Qwen (Alibaba)', models: [
    { id: 'qwen/qwen-2.5-72b-instruct', label: 'Qwen 2.5 72B', supports_chat: true, supports_vision: false, supports_voice: false },
    { id: 'qwen/qwen-2-vl-72b-instruct', label: 'Qwen 2 VL 72B (Vision)', supports_chat: true, supports_vision: true, supports_voice: false },
  ]},
]

const MODELS = {
  openrouter: OPENROUTER_GROUPS.flatMap(g => g.models),
  anthropic: [
    { id: 'claude-3-7-sonnet-20250219', label: "Claude 3.7 Sonnet (Yangi)", supports_chat: true, supports_vision: true, supports_voice: false },
    { id: 'claude-3-5-sonnet-20241022', label: "Claude 3.5 Sonnet", supports_chat: true, supports_vision: true, supports_voice: false },
    { id: 'claude-3-5-haiku-20241022',  label: 'Claude 3.5 Haiku', supports_chat: true, supports_vision: true, supports_voice: false },
    { id: 'claude-3-opus-20240229',   label: 'Claude 3 Opus', supports_chat: true, supports_vision: true, supports_voice: false },
    { id: 'claude-3-haiku-20240307',   label: 'Claude 3 Haiku (Eski)', supports_chat: true, supports_vision: true, supports_voice: false },
  ],
  openai: [
    { id: 'gpt-4o', label: 'GPT-4o (Eng aqlli)', supports_chat: true, supports_vision: true, supports_voice: false },
    { id: 'gpt-4o-mini', label: 'GPT-4o Mini (Tez va arzon)', supports_chat: true, supports_vision: true, supports_voice: false },
    { id: 'o1', label: 'o1 (Fikrlaydigan)', supports_chat: true, supports_vision: false, supports_voice: false },
    { id: 'o1-mini', label: 'o1 Mini', supports_chat: true, supports_vision: false, supports_voice: false },
    { id: 'o3-mini', label: 'o3 Mini', supports_chat: true, supports_vision: false, supports_voice: false },
    { id: 'gpt-4-turbo', label: 'GPT-4 Turbo', supports_chat: true, supports_vision: true, supports_voice: false },
    { id: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo', supports_chat: true, supports_vision: false, supports_voice: false },
    { id: 'whisper-1', label: 'Whisper 1 (Ovoz)', supports_chat: false, supports_vision: false, supports_voice: true },
  ],
  gemini: [
    { id: 'gemini-3.6-flash', label: 'Gemini 3.6 Flash (Yangi)', supports_chat: true, supports_vision: true, supports_voice: true },
    { id: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash', supports_chat: true, supports_vision: true, supports_voice: true },
    { id: 'gemini-flash-latest', label: 'Gemini Flash Latest', supports_chat: true, supports_vision: true, supports_voice: true },
    { id: 'gemini-pro-latest', label: 'Gemini Pro Latest', supports_chat: true, supports_vision: true, supports_voice: true },
    { id: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro', supports_chat: true, supports_vision: true, supports_voice: true },
    { id: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash (Eski)', supports_chat: true, supports_vision: true, supports_voice: true },
  ]
}

const PROVIDER_LOGOS = {
  openrouter: 'https://openrouter.ai/favicon.ico',
  anthropic: '/images/logo1.png',
  openai: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/04/ChatGPT_logo.svg/120px-ChatGPT_logo.svg.png',
  gemini: '/images/logo2.png'
}

const PROVIDER_LINKS = {
  openrouter: 'https://openrouter.ai/keys',
  anthropic: 'https://console.anthropic.com/settings/keys',
  openai: 'https://platform.openai.com/api-keys',
  gemini: 'https://aistudio.google.com/app/apikey'
}

const PROVIDER_DESCRIPTIONS = {
  openrouter: 'Bitta kalit orqali Claude, GPT, Gemini, Llama, DeepSeek va boshqa 200+ modelga kirish imkoni.',
  anthropic: 'Anthropic kompaniyasining Claude modellari.',
  openai: 'OpenAI kompaniyasining GPT modellari.',
  gemini: 'Google kompaniyasining Gemini modellari.',
}

// Native <option> ichida React komponenti (shu jumladan <Dot />) render qilinmaydi,
// shuning uchun model holati emoji doira o'rniga o'qiladigan matn bilan beriladi
// (DESIGN.md 8: holat faqat rang bilan berilmaydi).
const modelStatusSuffix = (mRes) =>
  mRes === true ? ' — ishlaydi' : (mRes === false ? ' — ishlamaydi' : '')

export default function AIOperatorCard() {
  const [cfg, setCfg] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)
  const [usage, setUsage] = useState(null)
  
  const [testing, setTesting] = useState({})
  const [testResults, setTestResults] = useState({})
  const [expandedKeys, setExpandedKeys] = useState({})
  const [showAdvancedGemini, setShowAdvancedGemini] = useState({})
  
  // Drag and Drop (DnD) Prioritization states
  const [dragKeyIndex, setDragKeyIndex] = useState(null)
  const [dragOverKeyIndex, setDragOverKeyIndex] = useState(null)

  const handleKeyDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', index.toString())
    e.dataTransfer.effectAllowed = 'move'
    setDragKeyIndex(index)
  }

  const handleKeyDragOver = (e, index) => {
    e.preventDefault()
    if (dragOverKeyIndex !== index) {
      setDragOverKeyIndex(index)
    }
  }

  const handleKeyDrop = async (e, dropIndex) => {
    e.preventDefault()
    if (dragKeyIndex === null || dragKeyIndex === dropIndex) {
      setDragKeyIndex(null)
      setDragOverKeyIndex(null)
      return
    }

    const currentKeys = cfg.keys ? [...cfg.keys] : []
    const [draggedItem] = currentKeys.splice(dragKeyIndex, 1)
    currentKeys.splice(dropIndex, 0, draggedItem)

    // First item automatically becomes primary
    const updatedKeys = currentKeys.map((k, idx) => ({
      ...k,
      is_primary: idx === 0
    }))

    setCfg(c => ({ ...c, keys: updatedKeys }))
    setDragKeyIndex(null)
    setDragOverKeyIndex(null)
    await save({ keys: updatedKeys, enabled: updatedKeys.length > 0 ? cfg.enabled : false })
  }
  
  // Add Key Modal
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [promptModalOpen, setPromptModalOpen] = useState(false)
  const [newKeyForm, setNewKeyForm] = useState({ provider: 'gemini', name: '', api_key: '' })
  const [addKeyError, setAddKeyError] = useState(null)
  // O'chirish tasdig'ini kutayotgan kalit (null = oyna yopiq)
  const [pendingDeleteKey, setPendingDeleteKey] = useState(null)

  const statusText = (tone, Icon, text) => (
    <span style={{ color: `var(--ui-st-${tone})`, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <Icon size={14} aria-hidden="true" /> {text}
    </span>
  )

  const getFriendlyError = (res) => {
    if (!res) return null;
    if (res.ok) return statusText('ok', HiCheckCircle, 'Ishlamoqda');
    const e = (res.error || '').toLowerCase();
    const k = res.kind || '';
    if (k === 'auth' || e.includes('key') || e.includes('unauthorized') || e.includes('invalid'))
      return statusText('critical', HiXCircle, "Noto'g'ri kalit");
    if (k === 'rate_limit' || e.includes('quota') || e.includes('balance') || e.includes('429'))
      return statusText('medium', HiXCircle, 'Limit tugagan');
    return statusText('critical', HiXCircle, 'Ishlamayapti');
  }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [c, u] = await Promise.all([aiApi.getConfig(), aiApi.usage().catch(() => null)])
      if (!c.system_prompt) c.system_prompt = c.default_system_prompt || '';
      if (c.keys) {
        c.keys = c.keys.map(k => ({ ...k, api_key: k.key_masked ? '***' : '' }));
      }
      setCfg(c); setUsage(u)
      
      // Auto expand primary key
      const primary = (c.keys || []).find(k => k.is_primary)
      if (primary) setExpandedKeys({ [primary.id]: true })
    } catch (e) { setMsg({ type: 'err', text: e.message }) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  const set = (k, v) => setCfg(c => ({ ...c, [k]: v }))

  const save = async (override = {}) => {
    setSaving(true); setMsg(null)
    try {
      const body = {
        enabled: cfg.enabled, rag_enabled: cfg.rag_enabled, system_prompt: cfg.system_prompt,
        max_tokens: cfg.max_tokens, max_faq: cfg.max_faq,
        monthly_budget_usd: cfg.monthly_budget_usd, auto_close: cfg.auto_close,
        human_typing: cfg.human_typing, handoff_enabled: cfg.handoff_enabled,
        idle_followup_min: cfg.idle_followup_min, idle_close_min: cfg.idle_close_min,
        post_close_message_uz: cfg.post_close_message_uz,
        post_close_message_uz_cy: cfg.post_close_message_uz_cy,
        post_close_message_ru: cfg.post_close_message_ru,
        offline_message: cfg.offline_message, keys: cfg.keys, ...override,
      }

      const updated = await aiApi.updateConfig(body)
      setCfg(updated)
      setMsg({ type: 'ok', text: 'Saqlandi' })
      aiApi.usage().then(setUsage).catch(() => {})
    } catch (e) { setMsg({ type: 'err', text: e.message }) }
    finally { setSaving(false) }
  }

  const handleAddKey = async () => {
    if (!newKeyForm.api_key.trim()) return setAddKeyError("Kalitni kiriting");
    
    setSaving(true);
    setAddKeyError(null);
    
    try {
      const payload = { 
        id: 'test_new', 
        provider: newKeyForm.provider,
        api_key: newKeyForm.api_key 
      };
      const res = await aiApi.test(payload);
      if (!res.ok) {
        let errStr = res.error || "Ushbu kalit yaroqsiz yoki tekshirishda xatolik yuz berdi";
        if (errStr.toLowerCase().includes("user not found")) {
          errStr = "OpenRouter API kaliti topilmadi (User not found). Iltimos, openrouter.ai/keys sahifasida 'Create Key' bosib yangi API kalit oling.";
        }
        setAddKeyError(errStr);
        setSaving(false);
        return;
      }
    } catch (e) {
      setAddKeyError(e.message);
      setSaving(false);
      return;
    }
    
    const isFirst = (cfg.keys || []).length === 0;
    const newKey = {
      id: '', // backend assigns UUID
      provider: newKeyForm.provider,
      name: newKeyForm.name || `${PROVIDERS.find(p => p.id === newKeyForm.provider)?.label} Kaliti`,
      api_key: newKeyForm.api_key,
      is_primary: isFirst,
      models: {}
    };
    
    await save({ keys: [...(cfg.keys || []), newKey] });
    setAddModalOpen(false);
    setNewKeyForm({ provider: 'gemini', name: '', api_key: '' });
  };

  const setPrimary = async (id) => {
    const newKeys = (cfg.keys || []).map(k => ({ ...k, is_primary: k.id === id }))
    // Also if AI wasn't enabled, enable it automatically since they chose a primary
    await save({ keys: newKeys, enabled: true })
  }
  
  const updateKeyModel = async (id, modelKey, modelValue) => {
    const newKeys = (cfg.keys || []).map(k => {
      if (k.id === id) {
         return { ...k, models: { ...k.models, [modelKey]: modelValue } }
      }
      return k
    })
    await save({ keys: newKeys })
  }

  const deleteKey = async (id) => {
    let newKeys = (cfg.keys || []).filter(k => k.id !== id)
    if (newKeys.length > 0 && !newKeys.some(k => k.is_primary)) {
      newKeys[0].is_primary = true
    }
    await save({ keys: newKeys, enabled: newKeys.length > 0 ? cfg.enabled : false })
  }

  const doTest = async (key) => {
    setTesting(p => ({ ...p, [key.id]: true }))
    setTestResults(p => ({ ...p, [key.id]: null }))
    try {
      const payload = { 
        id: key.id, 
        provider: key.provider,
        api_key: key.api_key,
        model: (key.models && key.models.chat) || '',
        test_all: true
      }
      const res = await aiApi.test(payload)
      setTestResults(p => ({ ...p, [key.id]: res }))
    } catch (e) {
      setTestResults(p => ({ ...p, [key.id]: { ok: false, error: e.message } }))
    } finally {
      setTesting(p => ({ ...p, [key.id]: false }))
    }
  }

  if (loading) return <Card><Loader state="solving" block /></Card>
  if (!cfg) return <Card><div style={{ padding: 20, color: 'var(--ui-st-critical)' }}>Xatolik yuz berdi</div></Card>

  const inputStyle = {
    width: '100%', padding: '8px 12px', border: '1px solid var(--ui-border)',
    borderRadius: 'var(--ui-r)', fontSize: 13,
    background: 'var(--ui-surface)', color: 'var(--ui-text-1)',
  }
  
  const activeKeys = cfg.keys || [];

  // O'chirish oynasi matni: nima yo'qolishini va oqibatini aniq aytadi (DESIGN.md 10)
  const pendingDeleteText = (() => {
    if (!pendingDeleteKey) return ''
    const name = pendingDeleteKey.name
      || PROVIDERS.find(p => p.id === pendingDeleteKey.provider)?.label
      || 'Kalit'
    const idx = activeKeys.indexOf(pendingDeleteKey)
    const base = `«${name}» kaliti butunlay o'chiriladi. Amalni qaytarib bo'lmaydi.`
    if (activeKeys.length === 1) return `${base} Bu oxirgi kalit — o'chgach avto AI operator o'chadi.`
    if (idx === 0 || pendingDeleteKey.is_primary) return `${base} Bu 1-prioritet kalit — o'rniga navbatdagi zaxira asosiy bo'ladi.`
    return base
  })()

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Card>
        <div className="card-body">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Avto AI operator</h2>
                <div style={{ fontSize: 13, color: 'var(--ui-text-3)', marginTop: 2 }}>Mijozlarga siz yo'qligingizda neyrotarmoq javob beradi</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {msg && <div style={{ fontSize: 13, color: msg.type === 'ok' ? 'var(--ui-st-ok)' : 'var(--ui-st-critical)', fontWeight: 600 }}>{msg.text}</div>}
              <Toggle on={cfg.enabled} onChange={v => set('enabled', v)} />
            </div>
          </div>
          
          {usage && (
            <div style={{ marginTop: 20, display: 'flex', gap: 16, padding: '12px 16px', background: 'var(--ui-surface-2)', borderRadius: 'var(--ui-r)', border: '1px solid var(--ui-border)', alignItems: 'center' }}>
              {/* BARCHA turdagi chaqiruvlar summasi — byudjet aynan shundan
                  hisoblanadi. Ilgari bu yerda faqat javoblar (month_cost_usd)
                  ko'rsatilar va admin ko'rgan summa byudjet chegarasiga mos
                  kelmasdi. */}
              <div style={{ fontSize: 13, color: 'var(--ui-text-2)' }}>Joriy oy xarajat: <span style={{ fontWeight: 600, color: 'var(--ui-text-1)' }}>${(usage.month_cost_total_usd ?? usage.month_cost_usd ?? 0).toFixed(3)}</span></div>
              <div style={{ fontSize: 13, color: 'var(--ui-text-2)' }}>Tokenlar (oylik): <span style={{ fontWeight: 600, color: 'var(--ui-text-1)' }}>{((usage.month_input_tokens || 0) + (usage.month_output_tokens || 0)).toLocaleString()} ta</span></div>
              <button className="btn btn-secondary" style={{ marginLeft: 'auto', fontSize: 12, padding: '4px 8px', height: 'auto' }} onClick={() => aiApi.usage().then(setUsage)}>
                <HiArrowPath size={14} /> Yangilash
              </button>
            </div>
          )}
        </div>
      </Card>

      {/* API Kalitlar panel */}
      <Card title="API Kalitlar va Model" actions={
        <button className="btn btn-primary" style={{ padding: '6px 12px', fontSize: 12, height: 'auto' }} onClick={() => setAddModalOpen(true)}>
          <HiPlus size={14} /> Yangi kalit qo'shish
        </button>
      }>
        <div className="card-body" style={{ paddingTop: 0 }}>
          
          {activeKeys.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', border: '1px dashed var(--ui-border)', borderRadius: 'var(--ui-r-lg)', background: 'var(--ui-surface)' }}>
              <div style={{ width: 48, height: 48, borderRadius: 'var(--ui-r)', background: 'var(--ui-surface-2)', border: '1px solid var(--ui-border)', color: 'var(--ui-text-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px auto' }}>
                <HiKey size={24} aria-hidden="true" />
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ui-text-1)' }}>Hali hech qanday API kalit qo'shilmagan</div>
              <div style={{ fontSize: 13, color: 'var(--ui-text-3)', marginTop: 4, maxWidth: 300, margin: '4px auto 0' }}>
                Avto-operator ishlashi uchun kamida bitta kalit qo'shing.
              </div>
            </div>
          ) : (
            <>
              {activeKeys.length > 1 && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                  background: 'var(--ui-surface-2)', border: '1px solid var(--ui-border)', borderRadius: 'var(--ui-r)',
                  marginBottom: 12, fontSize: 12, color: 'var(--ui-text-2)'
                }}>
                  <HiSparkles size={16} aria-hidden="true" style={{ color: 'var(--ui-text-2)', flexShrink: 0 }} />
                  <span>
                    <strong>Prioritet:</strong> Kalitlarni ushlash dastagidan
                    (<HiBars3 size={12} aria-hidden="true" style={{ verticalAlign: '-2px' }} />) yuqoriga/pastga suring.
                    1-o'rindagi kalit asosiy bo'ladi, qolganlari esa zaxira (fallback) bo'lib xizmat qiladi.
                  </span>
                </div>
              )}
              {activeKeys.map((key, idx) => {
                const p = PROVIDERS.find(pr => pr.id === key.provider) || PROVIDERS[0]
                const providerModels = MODELS[key.provider] || []
                const res = testResults[key.id]
                const isTesting = testing[key.id]
                const isExpanded = !!expandedKeys[key.id]
                const isPrimary = idx === 0 || key.is_primary
                const isLimitExhausted = res && res.kind === 'rate_limit'
                const isDragging = dragKeyIndex === idx
                const isDragOver = dragOverKeyIndex === idx

                return (
                  <div 
                    key={key.id || idx} 
                    draggable
                    onDragStart={e => handleKeyDragStart(e, idx)}
                    onDragOver={e => handleKeyDragOver(e, idx)}
                    onDrop={e => handleKeyDrop(e, idx)}
                    onDragEnd={() => { setDragKeyIndex(null); setDragOverKeyIndex(null); }}
                    style={{
                      marginBottom: idx === activeKeys.length - 1 ? 0 : 12,
                      border: `1px solid ${isDragOver || isPrimary ? 'var(--ui-text-1)' : 'var(--ui-border)'}`,
                      borderRadius: 'var(--ui-r-lg)',
                      background: isDragOver
                        ? 'var(--ui-surface-3)'
                        : (isPrimary ? 'var(--ui-surface-2)' : 'var(--ui-surface)'),
                      opacity: isDragging ? 0.4 : ((!isPrimary && !isExpanded) ? 0.85 : 1),
                      overflow: 'hidden',
                      transition: 'border-color var(--ui-dur) var(--ui-ease), background var(--ui-dur) var(--ui-ease), opacity var(--ui-dur) var(--ui-ease)'
                    }}
                  >
                    {/* Header */}
                    <div 
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', cursor: 'pointer' }}
                      onClick={() => setExpandedKeys(prev => ({...prev, [key.id]: !prev[key.id]}))}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {/* Drag Handle */}
                        <div
                          style={{
                            cursor: 'grab',
                            color: 'var(--ui-text-3)',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '4px 2px',
                            borderRadius: 'var(--ui-r)'
                          }}
                          title="Ushlab surish (Prioritetni o'zgartirish)"
                          aria-label="Ushlab surish (Prioritetni o'zgartirish)"
                          onClick={e => e.stopPropagation()}
                        >
                          <HiBars3 size={18} />
                        </div>

                        <img src={PROVIDER_LOGOS[key.provider]} alt={p.label} style={{ width: 20, height: 20, objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none' }} />
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{key.name || p.label}</div>

                        {/* Priority Indicators */}
                        {idx === 0 ? (
                          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ui-text-1)', background: 'var(--ui-surface-3)', border: '1px solid var(--ui-border-2)', padding: '2px 8px', borderRadius: 'var(--ui-r)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <HiStar size={12} aria-hidden="true" /> 1-Prioritet (Asosiy)
                          </span>
                        ) : (
                          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ui-text-3)', background: 'var(--ui-surface-2)', border: '1px solid var(--ui-border)', padding: '2px 8px', borderRadius: 'var(--ui-r)' }}>
                            {idx + 1}-Prioritet (Zaxira {idx})
                          </span>
                        )}

                        {isLimitExhausted && <span style={{ fontSize: 11, color: 'var(--ui-st-critical)', background: 'var(--ui-st-critical-bg)', padding: '2px 6px', borderRadius: 'var(--ui-r)' }}>Limit tugagan</span>}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <button 
                           className={!res ? "btn btn-secondary" : ""}
                           style={{
                              padding: '4px 10px', fontSize: 11, height: 'auto', borderRadius: 'var(--ui-r)', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                              background: !res ? 'var(--ui-surface)' : (res.ok ? 'var(--ui-st-ok-bg)' : 'var(--ui-st-critical-bg)'),
                              color: !res ? 'var(--ui-text-2)' : (res.ok ? 'var(--ui-st-ok)' : 'var(--ui-st-critical)'),
                              border: `1px solid ${!res ? 'var(--ui-border)' : (res.ok ? 'var(--ui-st-ok)' : 'var(--ui-st-critical)')}`,
                              opacity: isTesting ? 0.7 : 1
                           }}
                           title={res && res.working_models ? 'Ishlayotgan modellar: ' + res.working_models.join(', ') : ''}
                           onClick={(e) => { e.stopPropagation(); doTest(key); }}
                           disabled={isTesting}
                        >
                           {isTesting ? <Spinner size={12} /> : (
                              !res ? "Limitni tekshirish" : 
                              (res.ok ? `Limit yetarli (${res.working_models ? res.working_models.length : 1})` : (isLimitExhausted ? "Limit tugagan" : "Xatolik"))
                           )}
                        </button>

                        {!isPrimary && (
                          <button className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: 11, height: 'auto', background: 'var(--ui-surface)', opacity: isLimitExhausted ? 0.5 : 1, cursor: isLimitExhausted ? 'not-allowed' : 'pointer' }}
                                  onClick={(e) => { e.stopPropagation(); if (!isLimitExhausted) setPrimary(key.id); }} disabled={isLimitExhausted} title="1-O'ringa olib chiqish">
                            <HiStar size={12} aria-hidden="true" /> Asosiy qilish
                          </button>
                        )}
                        {isExpanded
                          ? <HiChevronUp size={18} aria-hidden="true" style={{ color: 'var(--ui-text-3)' }} />
                          : <HiChevronDown size={18} aria-hidden="true" style={{ color: 'var(--ui-text-3)' }} />}
                      </div>
                    </div>

                  {/* Body */}
                  {isExpanded && (
                    <div style={{ padding: '0 16px 16px 16px', borderTop: `1px ${isPrimary ? 'solid' : 'dashed'} var(--ui-border)` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, marginTop: 12 }}>
                         <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ui-text-2)' }}>API Kalit</div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                        <div style={{ position: 'relative', flex: 1 }}>
                          <input type="text" style={{ ...inputStyle, flex: 1 }} aria-label="API kalit"
                            value={key.api_key || ''} disabled autoComplete="off" />
                        </div>
                        <button className="btn" style={{ padding: '0 12px', background: 'var(--ui-surface)', color: 'var(--ui-st-critical)', border: '1px solid var(--ui-st-critical)' }} onClick={() => setPendingDeleteKey(key)} title="Kalitni o'chirish" aria-label="Kalitni o'chirish">
                          <HiTrash size={16} />
                        </button>
                      </div>

                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ui-text-2)', marginBottom: 8 }}>Model</div>
                      
                      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                             <select style={{...inputStyle, flex: 1}} aria-label="Suhbat modeli"
                                     value={(key.models || {}).chat || ''} 
                                     onChange={e => updateKeyModel(key.id, 'chat', e.target.value)} disabled={false}>
                               <option value="">-- Tanlang --</option>
                               {key.provider === 'openrouter'
                                 ? OPENROUTER_GROUPS.map(group => (
                                     <optgroup key={group.label} label={group.label}>
                                       {group.models.filter(m => m.supports_chat !== false).map(m => {
                                         const mRes = res?.model_results?.[m.id];
                                         const dot = modelStatusSuffix(mRes);
                                         return <option key={m.id} value={m.id}>{m.label}{dot}</option>;
                                       })}
                                     </optgroup>
                                   ))
                                 : providerModels.filter(m => m.supports_chat !== false).map(m => {
                                     const mRes = res?.model_results?.[m.id];
                                     const dot = modelStatusSuffix(mRes);
                                     return <option key={m.id} value={m.id}>{m.label}{dot}</option>;
                                   })
                               }
                             </select>
                          </div>
                          
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                             <input type="checkbox" id={`adv_${key.id}`} checked={showAdvancedGemini[key.id]} onChange={e => setShowAdvancedGemini(p => ({...p, [key.id]: e.target.checked}))} />
                             <label htmlFor={`adv_${key.id}`} style={{ fontSize: 12, cursor: 'pointer', userSelect: 'none', color: 'var(--ui-text-2)' }}>Qo'shimcha sozlamalar (Rasm va Ovoz modeli)</label>
                          </div>

                          {showAdvancedGemini[key.id] && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 12, background: 'var(--ui-surface-2)', borderRadius: 'var(--ui-r)', border: '1px solid var(--ui-border)' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                 <span style={{fontSize: 12, width: 45, fontWeight: 600, color: 'var(--ui-text-2)'}}>Vision:</span>
                                 <select style={{...inputStyle, flex: 1}} aria-label="Rasm (vision) modeli"
                                         value={(key.models || {}).vision || ''} 
                                         onChange={e => updateKeyModel(key.id, 'vision', e.target.value)} disabled={false}>
                                   <option value="">-- Tanlang --</option>
                                   {providerModels.filter(m => m.supports_vision).map(m => {
                                     const mRes = res?.model_results?.[m.id];
                                     const dot = modelStatusSuffix(mRes);
                                     return <option key={m.id} value={m.id}>{m.label}{dot}</option>;
                                   })}
                                 </select>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                 <span style={{fontSize: 12, width: 45, fontWeight: 600, color: 'var(--ui-text-2)'}}>Voice:</span>
                                 <select style={{...inputStyle, flex: 1}} aria-label="Ovoz (voice) modeli"
                                         value={(key.models || {}).voice || ''} 
                                         onChange={e => updateKeyModel(key.id, 'voice', e.target.value)} disabled={false}>
                                   <option value="">-- Tanlang --</option>
                                   {providerModels.filter(m => m.supports_voice).map(m => {
                                     const mRes = res?.model_results?.[m.id];
                                     const dot = modelStatusSuffix(mRes);
                                     return <option key={m.id} value={m.id}>{m.label}{dot}</option>;
                                   })}
                                 </select>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        
                      </div>
                      
                      {res && (
                        <div style={{ fontSize: 12, marginTop: 8, padding: 8, background: 'var(--ui-surface-2)', borderRadius: 'var(--ui-r)' }}>
                          {getFriendlyError(res)}
                        </div>
                      )}
                      
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                         <button className="btn btn-primary" style={{ padding: '6px 16px', fontSize: 12 }} onClick={() => save()} disabled={saving || isLimitExhausted}>
                           {saving ? <Spinner size={12}/> : 'Saqlash'}
                         </button>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
            </>
          )}
        </div>
      </Card>

      {/* Raqamli sozlamalar */}
      <Card title="Qo'shimcha parametrlar" actions={
        <button className="btn btn-primary" style={{ padding: '6px 16px', fontSize: 12, height: 'auto' }} onClick={() => save()} disabled={saving}>
          {saving ? <Spinner size={14}/> : 'Saqlash'}
        </button>
      }>
        <div className="card-body" style={{ paddingTop: 0 }}>
          <SettingsRow label="Javob uzunligi (token)" sub="Bitta javob uchun maksimal uzunlik">
            <input type="number" style={{...inputStyle, width: 80, textAlign: 'center'}} value={cfg.max_tokens} onChange={e => set('max_tokens', parseInt(e.target.value) || 250)} />
          </SettingsRow>
          
          <SettingsRow label="FAQ limit (ta)" sub="Bot xotirasiga olinadigan shablonlar soni">
            <input type="number" style={{...inputStyle, width: 80, textAlign: 'center'}} value={cfg.max_faq} onChange={e => set('max_faq', parseInt(e.target.value) || 8)} />
          </SettingsRow>
          
          <SettingsRow label="RAG (Agentic Search) Rejimi" sub="Katta FAQ bazalarida tokenlarni tejash uchun 2-bosqichli aqlli qidiruv. Yoqilganda javob biroz sekinroq lekin juda arzon bo'ladi.">
            <Toggle on={cfg.rag_enabled} onChange={v => set('rag_enabled', v)} />
          </SettingsRow>

          <SettingsRow label="Oylik byudjet ($)" sub="Xarajat chegarasi (ixtiyoriy)">
            <input type="number" step="0.5" style={{...inputStyle, width: 80, textAlign: 'center'}} value={cfg.monthly_budget_usd} onChange={e => set('monthly_budget_usd', parseFloat(e.target.value) || 0)} />
          </SettingsRow>

          <SettingsRow label="Jimlik eslatmasi (daqiqa)" sub="Mijoz yozmasa eslatish (0 = o'chirilgan)">
             <input type="number" style={{...inputStyle, width: 80, textAlign: 'center'}} value={cfg.idle_followup_min ?? 10} onChange={e => set('idle_followup_min', parseInt(e.target.value) || 0)} />
          </SettingsRow>

          <SettingsRow label="Avto-yopish vaqti (daqiqa)" sub="Mijoz javob bermasa chatni avtomatik yopish (0 = o'chirilgan)">
             <input type="number" style={{...inputStyle, width: 80, textAlign: 'center'}} value={cfg.idle_close_min ?? 30} onChange={e => set('idle_close_min', parseInt(e.target.value) || 0)} />
          </SettingsRow>
          
          <SettingsRow label="Odamdek yozish tezligi" sub="Javoblarni tez emas, harf-ma-harf jo'natish (mijoz bot ekanini sezmaydi)">
            <Toggle on={cfg.human_typing} onChange={v => set('human_typing', v)} />
          </SettingsRow>
          
          <SettingsRow label="Avto-yopish" sub="AI oxirgi javobni bergach, murojaatni avtomatik yopish">
            <Toggle on={cfg.auto_close} onChange={v => set('auto_close', v)} />
          </SettingsRow>

          <SettingsRow label="Katta operatorga uzatish" sub="Qiyin savolda AI murojaatni tirik operatorga uzatadi. O'chirilsa — uzatmaydi va o'zi javob berishda davom etadi">
            <Toggle on={cfg.handoff_enabled !== false} onChange={v => set('handoff_enabled', v)} />
          </SettingsRow>

          <SettingsRow label="Tizim ko'rsatmasi (System Prompt)" sub="Neyrotarmoq uchun xulq-atvor qoidalari va rollar">
            <button className="btn btn-secondary" style={{ padding: '6px 16px', fontSize: 12, height: 'auto' }} onClick={() => setPromptModalOpen(true)}>
              Ko'rish / O'zgartirish
            </button>
          </SettingsRow>

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ui-text-1)', marginBottom: 6 }}>Chat yakunlanganda dasturlangan javob (O'zbekcha)</div>
            <textarea style={{...inputStyle, height: 50, resize: 'vertical', fontSize: 13}} 
              value={cfg.post_close_message_uz || ''} 
              onChange={e => set('post_close_message_uz', e.target.value)} 
              placeholder="Murojaatingiz yakunlandi. Kiberxavfsizlik markazimiz har doim xizmatingizda!" />
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ui-text-1)', marginBottom: 6 }}>Chat yakunlanganda dasturlangan javob (Kirillcha)</div>
            <textarea style={{...inputStyle, height: 50, resize: 'vertical', fontSize: 13}} 
              value={cfg.post_close_message_uz_cy || ''} 
              onChange={e => set('post_close_message_uz_cy', e.target.value)} 
              placeholder="Мурожаатингиз якунланди. Киберхавфсизлик марказимиз ҳар доим хизматингизда!" />
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ui-text-1)', marginBottom: 6 }}>Chat yakunlanganda dasturlangan javob (Ruscha)</div>
            <textarea style={{...inputStyle, height: 50, resize: 'vertical', fontSize: 13}} 
              value={cfg.post_close_message_ru || ''} 
              onChange={e => set('post_close_message_ru', e.target.value)} 
              placeholder="Ваше обращение завершено. Наш центр кибербезопасности всегда к вашим услугам!" />
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ui-text-1)', marginBottom: 6 }}>Offline xabar</div>
            <textarea style={{...inputStyle, height: 60, resize: 'vertical', fontSize: 13}} 
              value={cfg.offline_message || ''} 
              onChange={e => set('offline_message', e.target.value)} 
              placeholder="API ishlamay qolganda mijozga nima deyish kerak?" />
          </div>
          
        </div>
      </Card>

      <Modal open={addModalOpen} onClose={() => setAddModalOpen(false)} title="Yangi API Kalit Qo'shish" footer={
        <div style={{ display: 'flex', gap: 10 }}>
           <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setAddModalOpen(false)}>Bekor qilish</button>
           <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleAddKey} disabled={saving}>{saving ? <Spinner size={14}/> : 'Qo\'shish'}</button>
        </div>
      }>
         <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '10px 0' }}>
            {addKeyError && (
              <div role="alert" style={{ padding: 12, borderRadius: 'var(--ui-r)', background: 'var(--ui-st-critical-bg)', border: '1px solid var(--ui-st-critical)', color: 'var(--ui-st-critical)', fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
                <HiXCircle size={18} aria-hidden="true" />
                <span>{addKeyError}</span>
              </div>
            )}
            <div>
               <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ui-text-2)', marginBottom: 6 }}>Provayder</div>
               <select style={inputStyle} aria-label="Provayder" value={newKeyForm.provider} onChange={e => setNewKeyForm(p => ({...p, provider: e.target.value}))}>
                  {PROVIDERS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
               </select>
               {PROVIDER_DESCRIPTIONS[newKeyForm.provider] && (
                 <div style={{ fontSize: 11, color: 'var(--ui-text-3)', marginTop: 5, display: 'flex', alignItems: 'center', gap: 5 }}>
                   {newKeyForm.provider === 'openrouter' && <span style={{ background: 'var(--ui-text-1)', color: 'var(--ui-surface)', fontSize: 11, padding: '1px 5px', borderRadius: 'var(--ui-r)', fontWeight: 600 }}>TAVSIYA ETILADI</span>}
                   {PROVIDER_DESCRIPTIONS[newKeyForm.provider]}
                 </div>
               )}
            </div>
            <div>
               <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ui-text-2)', marginBottom: 6 }}>Nomi (ixtiyoriy)</div>
               <input type="text" style={inputStyle} value={newKeyForm.name} onChange={e => setNewKeyForm(p => ({...p, name: e.target.value}))} placeholder={newKeyForm.provider === 'openrouter' ? 'Masalan: OpenRouter Asosiy' : 'Masalan: Asosiy Gemini Kaliti'} />
            </div>
            <div>
               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ui-text-2)', marginBottom: 6 }}>API Kalit</div>
                  <a href={PROVIDER_LINKS[newKeyForm.provider]} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: 'var(--ui-text-1)', textDecoration: 'underline', display: 'flex', alignItems: 'center', gap: 4 }}>
                    {newKeyForm.provider === 'openrouter'
                      ? <><HiKey size={12} aria-hidden="true" /> openrouter.ai/keys</>
                      : 'Qayerdan olaman?'}
                  </a>
               </div>
               <input type="password" style={inputStyle} value={newKeyForm.api_key} onChange={e => setNewKeyForm(p => ({...p, api_key: e.target.value}))} placeholder={newKeyForm.provider === 'openrouter' ? 'sk-or-v1-...' : 'sk-...'} />
            </div>
         </div>
      </Modal>

      {/* System Prompt Modal */}
      <Modal open={promptModalOpen} onClose={() => setPromptModalOpen(false)} title="Tizim ko'rsatmasi (System Prompt)" width={800} footer={
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', width: '100%' }}>
           <button className="btn btn-primary" style={{ padding: '8px 24px' }} onClick={() => { setPromptModalOpen(false); save(); }} disabled={saving}>
             {saving ? <Spinner size={14}/> : 'Saqlash va Yopish'}
           </button>
        </div>
      }>
         <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '10px 0' }}>
            <div style={{ fontSize: 13, color: 'var(--ui-text-2)', lineHeight: '1.5' }}>
               Bu matn AI botning boshlang'ich qoidalari va rolini belgilaydi. Uni iloji boricha aniq va tushunarli yozing. Bo'sh qoldirsangiz, tizimning standart ko'rsatmasi ishlatiladi.
            </div>
            
            <textarea 
              style={{ ...inputStyle, height: 350, resize: 'vertical', fontFamily: 'var(--ui-mono)', fontSize: 13, padding: 16, lineHeight: '1.6' }} 
              value={cfg.system_prompt ?? ''} 
              onChange={e => set('system_prompt', e.target.value)} 
              placeholder="Siz mijozlarga yordam beruvchi operatorsiz..." 
            />
            
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
               <button className="btn btn-secondary btn-sm" onClick={() => set('system_prompt', cfg.default_system_prompt)}>
                 Asliga qaytarish
               </button>
            </div>
         </div>
      </Modal>

      {/* Kalitni o'chirish tasdig'i — window.confirm o'rniga (DESIGN.md 10) */}
      <ConfirmModal
        open={!!pendingDeleteKey}
        onClose={() => setPendingDeleteKey(null)}
        onConfirm={() => {
          const key = pendingDeleteKey
          setPendingDeleteKey(null)
          if (key) deleteKey(key.id)
        }}
        title="Kalitni o'chirish"
        text={pendingDeleteText}
        confirmLabel="O'chirish"
        danger
      />

    </div>
  )
}
